import { ENGINE_VERSION, LOCATION_LABEL, EXCLUSION_LABEL, ALLOCATION_LABEL, type CalcResult, type Scenario } from "@h2lca/core";

import { DENOMINATOR_LABEL, H2_STATE_LABEL } from "@/components/workshop/helpers";

export interface ComparableRun {
  engineVersion: string;
  masterFingerprint: string;
  inputSnapshot: Scenario;
  result: CalcResult;
}

export interface ComparisonCondition { label: string; left: string; right: string; changed: boolean }

/** 저장된 조건과 결과만 비교한다. 현재 마스터로 과거 결과를 덮어 계산하지 않는다. */
export function compareRunConditions(left: ComparableRun, right: ComparableRun) {
  const rows: ComparisonCondition[] = [];
  const conditionChanges: string[] = [];
  const add = <T,>(label: string, a: T, b: T, condition = true, display?: (value: T) => string) => {
    const show = (v: unknown) => typeof v === "string" ? v : JSON.stringify(v);
    rows.push({ label, left: display ? display(a) : show(a), right: display ? display(b) : show(b), changed: JSON.stringify(a) !== JSON.stringify(b) });
    if (condition && JSON.stringify(a) !== JSON.stringify(b)) conditionChanges.push(label);
  };
  const a = left.inputSnapshot, b = right.inputSnapshot;
  add("산정방식", left.result.methodLabel, right.result.methodLabel);
  add("기능단위 수소량 (kg)", left.result.functionalUnitKg, right.result.functionalUnitKg);
  add("순도 (%)", a.functionalUnit.purityPct, b.functionalUnit.purityPct);
  add("압력 (bar)", a.functionalUnit.pressureBar, b.functionalUnit.pressureBar);
  add("상태", H2_STATE_LABEL[a.functionalUnit.state], H2_STATE_LABEL[b.functionalUnit.state]);
  add("인도지점", a.functionalUnit.deliveryPoint, b.functionalUnit.deliveryPoint);
  add("분모 기준", DENOMINATOR_LABEL[left.result.denominator.kind], DENOMINATOR_LABEL[right.result.denominator.kind]);
  add("분모 수소량 (kg)", left.result.denominator.kg, right.result.denominator.kg);
  const boundary = (r: ComparableRun) => r.result.byEquipment.map((e) => `${e.name}: ${LOCATION_LABEL[e.location as keyof typeof LOCATION_LABEL] ?? e.location}, ${e.inBoundary ? "포함" : `제외(${e.exclusionReason ? EXCLUSION_LABEL[e.exclusionReason] : "미기록"})`}`).join("\n");
  add("설비 구성·시스템 경계", boundary(left), boundary(right));
  add("부산물 배분", left.result.allocation.note, right.result.allocation.note);
  const allocationText = (v: Scenario["allocation"]) => [ALLOCATION_LABEL[v.policy], ...v.byproducts.map((p) => `${p.name}: ${p.amountKg} kg, ${p.lhvMJperKg} MJ/kg`), `근거: ${v.note || "미기록"}`].join("\n");
  add("배분 입력", a.allocation, b.allocation, true, allocationText);
  add("GWP 기준", left.result.gwpBasis, right.result.gwpBasis);
  add("기준연도", a.meta.baseYear, b.meta.baseYear);
  add("마스터 데이터 버전", left.masterFingerprint, right.masterFingerprint);
  add("산정 엔진", left.engineVersion, right.engineVersion);
  const electricityText = (v: Scenario["electricity"]) => [`입력 방식: ${v.mode === "share" ? "비율" : "사용량"}`, ...v.sources.map((s) => `${left.result.electricity.sources.find((f) => f.sourceId === s.sourceId)?.label ?? right.result.electricity.sources.find((f) => f.sourceId === s.sourceId)?.label ?? s.sourceId}: ${v.mode === "share" ? `${s.share * 100}%` : `${s.kWh ?? "미입력"} kWh`}`), `근거: ${v.note || "미기록"}`].join("\n");
  add("전력원 입력", a.electricity, b.electricity, false, electricityText);
  const flows = (s: Scenario) => s.equipment.filter((e) => e.h2.role !== "none").map((e) => `${e.name}: 투입 ${e.h2.inKg ?? "없음"} / 산출 ${e.h2.outKg ?? "없음"} / 손실 ${e.h2.lossKg ?? "없음"} kg`).join("\n");
  add("수소 흐름 입력", flows(a), flows(b), false);
  const overrides = (s: Scenario) => s.equipment.flatMap((e) => e.inputs.filter((i) => i.efId || i.customEf).map((i) => `${e.name} · ${i.label}: ${i.customEf ? `${i.customEf.value} ${i.customEf.unit}, ${i.customEf.source}, ${i.customEf.baseYear ?? "연도 미기록"}` : i.efId}`)).join("\n") || "없음";
  add("개별 지정 계수", overrides(a), overrides(b), false);
  const reasons = conditionChanges;
  if (left.result.status !== "ok" || right.result.status !== "ok") reasons.push("계산 제한 상태의 기록 포함");
  if (left.engineVersion !== ENGINE_VERSION || right.engineVersion !== ENGINE_VERSION) reasons.push("이전 엔진 기록: 현재 기준으로 재실행 필요");
  return { rows, reasons, comparable: reasons.length === 0 };
}
